import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import {
  TextField,
  Typography,
  Button,
  Container,
  Grid,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Divider,
  Avatar,
  Stack,
  Chip,
  Box,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  alpha,
  CircularProgress,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import debounce from 'lodash.debounce';
import { getErrorMessage } from '../utils/apiError';
import districtData from '../../src/data/districts.json';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { QRCodeSVG } from 'qrcode.react';
import DownloadIcon from '@mui/icons-material/Download';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Person,
  Email,
  Phone,
  LocationOn,
  Check,
  ArrowForward,
  Home
} from '@mui/icons-material';

const PageContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  padding: theme.spacing(4),
}));

const SignupPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: 800,
  borderRadius: theme.shape.borderRadius * 2,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontSize: '28px',
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  textAlign: 'center',
  marginBottom: theme.spacing(1),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 1.5,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.95),
      transform: 'translateY(-1px)',
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.background.paper, 1),
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 3,
  padding: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 5px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 1.5,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
  },
}));

const UserCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'visible',
  background: 'linear-gradient(135deg, #fff 0%, #f5f5f5 100%)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
}));

const SuccessDialog = ({ open, onClose, userData, successUid }) => {
  const cardRef = React.useRef(null);

  const downloadAsPDF = async () => {
    const card = cardRef.current;
    try {
      const canvas = await html2canvas(card, {
        scale: 2,
        backgroundColor: '#ffffff',
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`user-card-${successUid}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const InfoItem = ({ icon, label, value }) => (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
      {icon}
      <Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body1" fontWeight="500">
          {value || 'Not provided'}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 3
        }
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
          Welcome to Gold Petals!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your account has been created successfully
        </Typography>
      </Box>

      <UserCard>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                margin: '0 auto',
                bgcolor: 'primary.main',
                fontSize: '3rem'
              }}
            >
              {userData.first_name[0]}
            </Avatar>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              {`${userData.prefix} ${userData.first_name} ${userData.last_name}`}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'white', 
                py: 0.5, 
                px: 2, 
                borderRadius: 2,
                display: 'inline-block'
              }}
            >
              UID: {successUid}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
               Default Password: Admin@123
               </Typography>
            <Box sx={{ mt: 3 }}>
              <QRCodeCanvas 
                value={successUid}
                size={100}
                level="H"
                includeMargin={true}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <InfoItem
                  icon={<Person color="primary" />}
                  label="Display Name"
                  value={userData.display_name}
                />
                <InfoItem
                  icon={<Email color="primary" />}
                  label="Email"
                  value={userData.email_id}
                />
                <InfoItem
                  icon={<Phone color="primary" />}
                  label="Phone"
                  value={userData.phone_number}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoItem
                  icon={<LocationOn color="primary" />}
                  label="Address"
                  value={`${userData.address}, ${userData.village_name}`}
                />
                <InfoItem
                  icon={<LocationOn color="primary" />}
                  label="Area"
                  value={`${userData.area}, ${userData.district}`}
                />
                <InfoItem
                  icon={<LocationOn color="primary" />}
                  label="State"
                  value={`${userData.state} - ${userData.pincode}`}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </UserCard>

      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <ActionButton
          fullWidth
          variant="contained"
          onClick={downloadAsPDF}
          startIcon={<DownloadIcon />}
        >
          Download Card
        </ActionButton>
        <ActionButton
          fullWidth
          variant="contained"
          onClick={onClose}
          startIcon={<PersonIcon />}
        >
          Continue to Login
        </ActionButton>
      </Stack>
    </Dialog>
  );
};

const RegisterForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Personal Details', 'Contact Information', 'Address Details'];
  
  const [formData, setFormData] = useState({
    prefix: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    display_name: '',
    phone_number: '',
    email_id: '',
    address: '',
    area: '',
    village_name:'',
    district: '',
    state: '',
    pincode: '',
  });

  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [otpSentTo, setOtpSentTo] = useState(null);
  const [pincode, setPincode] = useState();
  const [fetchedData, setFetchedData] = useState([]);
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [pincodeError, setPincodeError] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successUid, setSuccessUid] = useState('');
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Define the debounced function
  const fetchData = useCallback(
    debounce(async (pin) => {
      try {
        setIsFetchingLocation(true);
        const response = await axios.get(`${API_BASE_URL}/fetch-city?pincode=${pin}`);
        setFetchedData(response.data);
        setPincodeError('');
      } catch (error) {
        console.error('Error fetching data:', error);
        setPincodeError('Pincode not allowed');
        setFetchedData([]);
      } finally {
        setIsFetchingLocation(false);
      }
    }, 300),
    []
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    if (name === 'pincode') {
      // Only allow numbers and limit to 6 digits
      const cleanedValue = value.replace(/\D/g, '').slice(0, 6);
      setPincode(cleanedValue);
      setFormData(prev => ({
        ...prev,
        pincode: cleanedValue
      }));

      // Fetch data only if the pincode has 6 digits
      if (cleanedValue.length === 6) {
        fetchData(cleanedValue);
      } else {
        setFetchedData([]);
      }
    } else {
      // Handle all other form fields
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleVillageChange = (event) => {
    const villageName = event.target.value;

    // Adjust key names to match the correct ones from your database fields
    const selected = fetchedData.find(item => item['village_name'] === villageName);

    // Set the selected village
    setSelectedVillage(selected);
  
    // Update the form data with the selected village details
    setFormData({
      ...formData,
      village_name: selected ? selected.village_name :'',
      area: selected ? selected['subdistrict_name'] : '',
      state: selected ? selected['state_name'] : '',
      district: selected ? selected['district_name'] : '',
    });
  };

  const validatephone_number = (phoneNumber) => {
    return /^[6-9]\d{9}$/.test(phoneNumber); // Validates Indian mobile numbers starting with 6-9 and exactly 10 digits
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendOtp = (type) => {
    const data = type === 'phone' ? { phone_number: formData.phone_number } : { email_id: formData.email_id };
    setFormError('');
    axios.post(`${API_BASE_URL}/send-otp`, data)
      .then((response) => {
        setOtpSentTo(type);
        setFormSuccess(response.data.message || 'OTP sent successfully');
      })
      .catch((error) => {
        setFormError(getErrorMessage(error));
      });
  };

  const handleVerifyOtp = (type) => {
    const otp = type === 'phone' ? formData.otpPhone : formData.otpEmail;
    const data = { otp, type, phone_number: formData.phone_number, email_id: formData.email_id };
    setFormError('');
    axios.post(`${API_BASE_URL}/verify-otp`, data)
      .then((response) => {
        if (type === 'phone') setIsPhoneVerified(true);
        else setIsEmailVerified(true);
        setFormSuccess('OTP verified successfully!');
      })
      .catch((error) => {
        setFormError(getErrorMessage(error));
      });
  };

  const navigate = useNavigate();
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isFetchingLocation) {
      setFormError("Please wait, fetching village details...");
      return;
    }

    if (!selectedVillage) {
      setFormError("Location data not loaded yet. Please select a village.");
      return;
    }

    // Add validation check
    if (!validatephone_number(formData.phone_number)) {
      setFormError('Please enter a valid 10-digit phone number');
      return;
    }

    // const stateAbbreviations = {
    //   "Andaman and Nicobar Islands": "AN",
    //   "Andhra Pradesh": "AP",
    //   "Arunachal Pradesh": "AR",
    //   "Assam": "AS",
    //   "Bihar": "BR",
    //   "Chandigarh": "CH",
    //   "Chhattisgarh": "CT",
    //   "Dadra and Nagar Haveli and Daman and Diu": "DN",
    //   "Delhi": "DL",
    //   "Goa": "GA",
    //   "Gujarat": "GJ",
    //   "Haryana": "HR",
    //   "Himachal Pradesh": "HP",
    //   "Jammu and Kashmir": "JK",
    //   "Jharkhand": "JH",
    //   "Karnataka": "KA",
    //   "Kerala": "KL",
    //   "Ladakh": "LA",
    //   "Lakshadweep": "LD",
    //   "Madhya Pradesh": "MP",
    //   "Maharashtra": "MH",
    //   "Manipur": "MN",
    //   "Meghalaya": "ML",
    //   "Mizoram": "MZ",
    //   "Nagaland": "NL",
    //   "Odisha": "OD",
    //   "Puducherry": "PY",
    //   "Punjab": "PB",
    //   "Rajasthan": "RJ",
    //   "Sikkim": "SK",
    //   "Tamil Nadu": "TN",
    //   "Telangana": "TG",
    //   "Tripura": "TR",
    //   "Uttar Pradesh": "UP",
    //   "Uttarakhand": "UK",
    //   "West Bengal": "WB"
    // };

    // const generateUniqueId = (data) => {
    //   console.log('=============>_____1___<==============',data)
    //   const stateAbbreviation = stateAbbreviations[data?.['state_name']] || 'XX'; // Default to 'XX' if not found
    //   const districtCode = data?.['district_code'];
    //   const villageCode = data?.['village_code'];

    //   // Use the updated and correctly padded `lastGeneratedId`
    //   const incrementalId = Math.floor(100000 + Math.random() * 900000); // Generates a random 6-digit number

    //   // Combine all parts to form the unique ID
    //   const uniqueId = `91${stateAbbreviation}${districtCode}${villageCode}${incrementalId}`;
    //   console.log('Generated Unique ID:', uniqueId);

    //   return uniqueId;
    // };

    // const uniqueId = generateUniqueId(selectedVillage);

    // Merge formData with dynamically selected village data
    
    
    const stateAbbreviations = {
      "Andaman And Nicobar Islands": "AN", "Andhra Pradesh": "AP",
      "Arunachal Pradesh": "AR", "Assam": "AS", "Bihar": "BR",
      "Chandigarh": "CH", "Chhattisgarh": "CG",
      "The Dadra And Nagar Haveli And Daman And Diu": "DN", "Delhi": "DL",
      "Goa": "GA", "Gujarat": "GJ", "Haryana": "HR",
      "Himachal Pradesh": "HP", "Jammu And Kashmir": "JK",
      "Jharkhand": "JH", "Karnataka": "KA", "Kerala": "KL",
      "Ladakh": "LA", "Lakshadweep": "LD", "Madhya Pradesh": "MP",
      "Maharashtra": "MH", "Manipur": "MN", "Meghalaya": "ML",
      "Mizoram": "MZ", "Nagaland": "NL", "Odisha": "OD",
      "Puducherry": "PY", "Punjab": "PB", "Rajasthan": "RJ",
      "Sikkim": "SK", "Tamil Nadu": "TN", "Telangana": "TG",
      "Tripura": "TR", "Uttar Pradesh": "UP", "Uttarakhand": "UK",
      "West Bengal": "WB"
    };

    const generateUniqueId = (villageData) => {
      // Step 1: Match state name to abbreviation
      // district.json uses Title Case like "Uttar Pradesh" — match directly
      const stateName = (villageData?.state_name || '').trim();
      const stateCode = stateAbbreviations[stateName] || 'IN';

      // Step 2: Find district in district.json by matching district_name
      // district.json District Name is Title Case e.g. "Lucknow"
      // selectedVillage.district_name may vary in case — normalize both sides
      const districtName = (villageData?.district_name || '').trim().toLowerCase();

      const matchedDistrict = districtData.find(
        d => d['District Name (In English)'].toLowerCase() === districtName
          && d['State Name (In English)'].toLowerCase() === stateName.toLowerCase()
      );

      // Step 3: Use District Code from JSON — pad to 3 digits for consistency
      // e.g. District Code 162 (Lucknow) → "162"
      // Fallback to "000" if district not found
      const districtCode = matchedDistrict
        ? String(matchedDistrict['District Code']).padStart(3, '0')
        : '000';

      // Step 4: Generate 6 digit unique number
      // Combining timestamp + random reduces duplicate chance in production
      const timestamp = Date.now() % 1000000;
      const random = Math.floor(Math.random() * 1000000);
      const combined = String((timestamp + random) % 1000000).padStart(6, '0');

      // Final ID: 91 + UP + 162 + 483921 = 91UP162483921
      const uid = `91${stateCode}${districtCode}${combined}`;
      console.log('Generated UID:', uid, '| District matched:', matchedDistrict?.['District Name (In English)'] || 'NOT FOUND');
      return uid;
    };

    const finalFormData = {
      ...formData,
      password: 'Admin@123',
      village_name:selectedVillage ? selectedVillage.village_name : formData.village_name,
      area: selectedVillage ? selectedVillage['subdistrict_name'] : formData.area,
      state: selectedVillage ? selectedVillage['state_name'] : formData.state,
      district: selectedVillage ? selectedVillage['district_name'] : formData.district,
      address: formData.address || '', // Ensure address is included
    };

    try {
      setSubmitting(true);
      setFormError('');
      const response = await axios.post(`${API_BASE_URL}/signup`, finalFormData);
      setSuccessUid(response.data?.uid || '');
      setShowSuccessDialog(true);
    } catch (err) {
      setFormError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    setShowSuccessDialog(false);
    navigate('/login/');
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={2}>
            <StyledFormControl fullWidth>
              <InputLabel>Prefix</InputLabel>
              <Select
                name="prefix"
                value={formData.prefix}
                onChange={handleInputChange}
                label="Prefix"
              >
                <MenuItem value="Mr">Mr</MenuItem>
                <MenuItem value="Mrs">Mrs</MenuItem>
                <MenuItem value="Ms">Ms</MenuItem>
                <MenuItem value="Dr">Dr</MenuItem>
                <MenuItem value="Eng">Eng</MenuItem>
                <MenuItem value="Miss">Miss</MenuItem>
                <MenuItem value="Master">Master</MenuItem>
              </Select>
            </StyledFormControl>

            <StyledTextField
              required
              fullWidth
              label="First Name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              fullWidth
              label="Middle Name"
              name="middle_name"
              value={formData.middle_name}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              required
              fullWidth
              label="Last Name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              required
              fullWidth
              label="Display Name"
              name="display_name"
              value={formData.display_name}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={2}>
            <StyledTextField
              required
              fullWidth
              label="Phone Number"
              name="phone_number"
              value={formData.phone_number}
              onChange={(e) => {
                // Only allow numbers and limit to 10 digits
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                handleInputChange({
                  target: {
                    name: 'phone_number',
                    value: value
                  }
                });
              }}
              inputProps={{
                maxLength: 10,  
                pattern: '[0-9]*',
                inputMode: 'numeric'
              }}
              error={formData.phone_number.length > 0 && formData.phone_number.length !== 10}
              helperText={
                formData.phone_number.length > 0 && formData.phone_number.length !== 10
                  ? 'Phone number must be 10 digits'
                  : ''
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="primary" />
                  </InputAdornment>
                ),
              }}
            />
            {!isPhoneVerified && otpSentTo === 'phone' && (
              <Box>
                <StyledTextField
                  required
                  fullWidth
                  label="Phone OTP"
                  name="otpPhone"
                  value={formData.otpPhone || ''}
                  onChange={handleInputChange}
                />
                <ActionButton
                  variant="contained"
                  onClick={() => handleVerifyOtp('phone')}
                  sx={{ mt: 1 }}
                >
                  Verify Phone OTP
                </ActionButton>
              </Box>
            )}

            <StyledTextField
              required
              fullWidth
              label="Email"
              name="email_id"
              value={formData.email_id}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="primary" />
                  </InputAdornment>
                ),
              }}
            />
            {!isEmailVerified && otpSentTo === 'email' && (
              <Box>
                <StyledTextField
                  required
                  fullWidth
                  label="Email OTP"
                  name="otpEmail"
                  value={formData.otpEmail || ''}
                  onChange={handleInputChange}
                />
                <ActionButton
                  variant="contained"
                  onClick={() => handleVerifyOtp('email')}
                  sx={{ mt: 1 }}
                >
                  Verify Email OTP
                </ActionButton>
              </Box>
            )}
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={2}>
            <StyledTextField
              required
              fullWidth
              label="Pincode"
              name="pincode"
              value={formData.pincode}
              onChange={(e) => {
                // Only allow numbers and limit to 6 digits
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                handleInputChange({
                  target: {
                    name: 'pincode',
                    value: value
                  }
                });
              }}
              inputProps={{
                maxLength: 6,
                pattern: '[0-9]*',
                inputMode: 'numeric'
              }}
              error={formData.pincode?.length > 0 && formData.pincode?.length !== 6}
              helperText={
                formData.pincode?.length > 0 && formData.pincode?.length !== 6
                  ? 'Pincode must be 6 digits'
                  : ''
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            {isFetchingLocation && (
              <Typography variant="caption" color="primary" sx={{ display: 'block', mt: -1, mb: 1, ml: 1 }}>
                Fetching village details...
              </Typography>
            )}

            {fetchedData.length > 0 && (
              <StyledFormControl fullWidth>
                <InputLabel>Select Village</InputLabel>
                <Select
                  value={selectedVillage ? selectedVillage['village_name'] : ''}
                  onChange={handleVillageChange}
                  label="Select Village"
                >
                  {fetchedData.map((item, index) => (
                    <MenuItem key={index} value={item['village_name']}>
                      {item['village_name']}
                    </MenuItem>
                  ))}
                </Select>
              </StyledFormControl>
            )}

            {selectedVillage && (
              <>
                <StyledTextField
                  required
                  fullWidth
                  label="State"
                  value={selectedVillage['state_name']}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
                <StyledTextField
                  required
                  fullWidth
                  label="District"
                  value={selectedVillage['district_name']}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
                <StyledTextField
                  required
                  fullWidth
                  label="Subdistrict"
                  value={selectedVillage['subdistrict_name']}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
                <StyledTextField
                  required
                  fullWidth
                  label="House No./ Plot No./ Sector"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Home color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />

<StyledTextField
  fullWidth
  label="From Year"
  name="from_year"
  type="number"
  value={formData.from_year || ''}
  onChange={handleInputChange}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        📅
      </InputAdornment>
    ),
  }}
  inputProps={{
    min: 1900,
    max: new Date().getFullYear(),
  }}
/>

<StyledTextField
  fullWidth
  label="To Year"
  name="to_year"
  type="number"
  value={formData.to_year || ''}
  onChange={handleInputChange}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        📅
      </InputAdornment>
    ),
  }}
  inputProps={{
    min: 1900,
    max: new Date().getFullYear(),
  }}
/>

              </>
            )}
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <PageContainer maxWidth={false}>
      <SignupPaper elevation={3}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Logo>GOLD PETALS</Logo>
          <Typography variant="h5" gutterBottom sx={{ 
            fontWeight: 700,
            background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}>
            Create Your Account
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={handleSubmit}>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>
              {formError}
            </Alert>
          )}
          {formSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFormSuccess('')}>
              {formSuccess}
            </Alert>
          )}
          {renderStepContent(activeStep)}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              sx={{ borderRadius: 3 }}
            >
              Back
            </Button>
            {activeStep === steps.length - 1 ? (
              <ActionButton
                type="submit"
                variant="contained"
                endIcon={submitting || isFetchingLocation ? <CircularProgress size={18} color="inherit" /> : <Check />}
                disabled={submitting || isFetchingLocation}
              >
                {submitting ? 'Registering...' : 'Complete Registration'}
              </ActionButton>
            ) : (
              <ActionButton
                onClick={handleNext}
                variant="contained"
                endIcon={<ArrowForward />}
              >
                Next Step
              </ActionButton>
            )}
          </Box>
        </form>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Button
              color="primary"
              onClick={() => navigate('/login')}
              sx={{ textTransform: 'none' }}
            >
              Log In
            </Button>
          </Typography>
        </Box>
      </SignupPaper>

      <SuccessDialog
        open={showSuccessDialog}
        onClose={handleDialogClose}
        userData={formData}
        successUid={successUid}
      />
    </PageContainer>
  );
};

export default RegisterForm;
